import {
  BRIDGE_MESSAGE_SOURCES,
  BRIDGE_MESSAGE_TARGETS,
  createBridgeEnvelope,
} from "@bezoned/desktop-bridge/bridge-common.js";
import { DESKTOP_SYNC_MESSAGE_TYPES } from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import type {
  DesktopSyncMessageType,
  DesktopSyncSections,
} from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import type { PttUiohookPlatform } from "@bezoned/shared/features/push-to-talk/push-to-talk.types.js";
import {
  formatBrowserCode,
  getPttUiohookConfig,
} from "@bezoned/shared/features/push-to-talk/push-to-talk.utils.js";
import { AppEventNames } from "@bezoned/shared/types/app-event-names.js";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { appEventCreate } from "../../services/api.service.js";
import { useUserImageStore } from "../../store/user-image.store.js";
import type { UserPhotoStatus } from "../../store/user-image.store.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import { useOffice } from "../office/use-office.js";
import { PresenceContext } from "../presence/presence.context.js";
import { usePushToTalkStore } from "../table-call/push-to-talk.store.js";
import { simpleStringHash } from "./desktop-bridge-sync.utils.js";
import { useDesktopBridgeRoom } from "./use-desktop-bridge-room.js";
import { useDesktopBridge } from "./use-desktop-bridge.js";

const getBrowserPttPlatform = (): PttUiohookPlatform => {
  if (typeof navigator === "undefined") {
    return "linux";
  }

  const platform = navigator.platform.toLowerCase();

  if (platform.includes("mac")) {
    return "darwin";
  }

  if (platform.includes("win")) {
    return "win32";
  }

  return "linux";
};

const createPttSection = ({
  keyCode,
  platform,
}: {
  keyCode: string;
  platform: PttUiohookPlatform;
}): NonNullable<DesktopSyncSections["ptt"]> | null =>
  getPttUiohookConfig(keyCode, platform);

const logPttSelectionSync = ({
  bridgeMessageType,
  keyCode,
  platform,
  resolvedConfig,
}: {
  bridgeMessageType: DesktopSyncMessageType;
  keyCode: string;
  platform: PttUiohookPlatform;
  resolvedConfig: NonNullable<DesktopSyncSections["ptt"]>;
}): void => {
  console.info("[DesktopBridgePTT]", {
    bridgeMessageType,
    platform,
    resolvedConfig,
    selectedKeyCode: keyCode,
    selectedKeyDisplay: formatBrowserCode(keyCode),
  });
};

const SYNC_SECTION_KEYS: (keyof DesktopSyncSections)[] = [
  "ptt",
  "profile",
  "presence",
  "team",
];

interface AvatarPhotoSnapshot {
  pictureUrl: string | null;
  photoStatus: UserPhotoStatus | null;
}

interface AvatarPictureUpdate {
  pictureUrl?: string | null;
}

const getAvatarPhotoSnapshot = (userId: string): AvatarPhotoSnapshot => {
  const store = useUserImageStore.getState();

  return {
    photoStatus: store.getPhotoStatus(userId),
    pictureUrl: store.getPhoto(userId),
  };
};

const hasAvatarPhotoSnapshotChanged = ({
  currentPictureUrl,
  currentPhotoStatus,
  nextSnapshot,
}: {
  currentPictureUrl: string | null;
  currentPhotoStatus: UserPhotoStatus | null;
  nextSnapshot: AvatarPhotoSnapshot;
}): boolean =>
  currentPictureUrl !== nextSnapshot.pictureUrl ||
  currentPhotoStatus !== nextSnapshot.photoStatus;

const shouldClearSyncedAvatar = (
  photoStatus: UserPhotoStatus | null
): boolean => photoStatus === "missing";

const syncMissingAvatarSnapshot = ({
  nextSnapshot,
  setAvatarPictureUpdate,
}: {
  nextSnapshot: AvatarPhotoSnapshot;
  setAvatarPictureUpdate: (value: AvatarPictureUpdate) => void;
}): boolean => {
  if (nextSnapshot.pictureUrl) {
    return false;
  }

  if (shouldClearSyncedAvatar(nextSnapshot.photoStatus)) {
    setAvatarPictureUpdate({ pictureUrl: null });
  }

  return true;
};

const warnIfEmptyOnConnect = (
  type: DesktopSyncMessageType,
  sections: DesktopSyncSections
): void => {
  if (
    type === DESKTOP_SYNC_MESSAGE_TYPES.ON_CONNECT &&
    sections.ptt === undefined &&
    sections.profile === undefined &&
    sections.presence === undefined &&
    sections.team === undefined &&
    sections.chat === undefined
  ) {
    console.warn(
      "[DesktopBridgeConnectSync] on-connect message has no section data"
    );
  }
};

export const DesktopBridgeConnectSyncListener = () => {
  const { user } = useAuth();
  const { team } = useOffice();
  const presenceContext = useContext(PresenceContext);
  if (!presenceContext.__ctx_init) {
    throw new Error(
      "DesktopBridgeConnectSyncListener must be used within PresenceProvider"
    );
  }
  const { presence } = presenceContext;
  const {
    status: bridgeStatus,
    instanceId,
    sendJson,
    desktopAppVersion,
  } = useDesktopBridge();
  const code = usePushToTalkStore((state) => state.code);
  const pttPlatform = useMemo(() => getBrowserPttPlatform(), []);

  const [avatarPictureUpdate, setAvatarPictureUpdate] =
    useState<AvatarPictureUpdate>({});
  const avatarPictureUrlRef = useRef<string | null>(null);
  const avatarPhotoStatusRef = useRef<UserPhotoStatus | null>(null);
  const lastSectionSignatureRef = useRef<
    Partial<Record<keyof DesktopSyncSections, string>>
  >({});
  const onConnectSyncStateRef = useRef<"idle" | "sending" | "sent">("idle");
  const sidebarConnectedReportedRef = useRef<"idle" | "sending" | "sent">(
    "idle"
  );

  const bridgeRoom = useDesktopBridgeRoom();

  const isBridgeReady = bridgeStatus === "ready" && !!instanceId;

  const pttSection = useMemo(() => {
    const nextPttSection = createPttSection({
      keyCode: code,
      platform: pttPlatform,
    });

    if (nextPttSection) {
      return nextPttSection;
    }

    recordErrorInSentry(
      new Error("Unable to resolve push-to-talk config for desktop sync"),
      {
        extra: {
          code,
          platform: pttPlatform,
        },
        tags: {
          event: "push-to-talk-sync-config-invalid",
          feature: "push-to-talk",
        },
      }
    );
    return null;
  }, [code, pttPlatform]);

  const profileSection = useMemo(
    () => ({
      displayName: user.name,
      userId: user.id,
      ...avatarPictureUpdate,
    }),
    [avatarPictureUpdate, user.id, user.name]
  );

  const presenceSection = useMemo(
    () => ({
      availability: presence?.availability ?? "offline",
      outOfOffice: presence?.outOfOffice?.isOutOfOffice ?? false,
      statusMessage: presence?.statusMessage ?? null,
    }),
    [presence]
  );

  const teamSection = useMemo(
    () => ({
      msTeamId: team?.msTeamId ?? null,
    }),
    [team?.msTeamId]
  );

  const sendSyncMessage = useCallback(
    (type: DesktopSyncMessageType, sections: DesktopSyncSections): void => {
      if (!isBridgeReady) {
        return;
      }

      let hasAnySectionChanges = false;

      for (const sectionKey of SYNC_SECTION_KEYS) {
        const sectionValue = sections[sectionKey];
        if (sectionValue === undefined) {
          continue;
        }

        const sectionSignature = String(
          simpleStringHash(JSON.stringify(sectionValue))
        );
        if (lastSectionSignatureRef.current[sectionKey] === sectionSignature) {
          continue;
        }

        lastSectionSignatureRef.current[sectionKey] = sectionSignature;
        hasAnySectionChanges = true;
      }

      warnIfEmptyOnConnect(type, sections);

      if (
        !hasAnySectionChanges &&
        type !== DESKTOP_SYNC_MESSAGE_TYPES.ON_CONNECT
      ) {
        return;
      }

      sendJson(
        bridgeRoom,
        createBridgeEnvelope({
          instanceId,
          payload: {
            sections,
          },
          source: BRIDGE_MESSAGE_SOURCES.FRONTEND,
          target: BRIDGE_MESSAGE_TARGETS.DESKTOP,
          type,
        })
      );
    },
    [bridgeRoom, isBridgeReady, instanceId, sendJson]
  );

  useEffect(() => {
    setAvatarPictureUpdate({});
    avatarPictureUrlRef.current = null;
    avatarPhotoStatusRef.current = null;

    const syncAvatar = () => {
      const nextSnapshot = getAvatarPhotoSnapshot(user.id);

      if (
        !hasAvatarPhotoSnapshotChanged({
          currentPhotoStatus: avatarPhotoStatusRef.current,
          currentPictureUrl: avatarPictureUrlRef.current,
          nextSnapshot,
        })
      ) {
        return;
      }

      avatarPictureUrlRef.current = nextSnapshot.pictureUrl;
      avatarPhotoStatusRef.current = nextSnapshot.photoStatus;

      if (
        syncMissingAvatarSnapshot({
          nextSnapshot,
          setAvatarPictureUpdate,
        })
      ) {
        return;
      }

      setAvatarPictureUpdate({ pictureUrl: nextSnapshot.pictureUrl });
    };

    syncAvatar();

    const unsubscribe = useUserImageStore.subscribe(() => {
      syncAvatar();
    });

    return () => {
      unsubscribe();
    };
  }, [user.id]);

  useEffect(() => {
    if (bridgeStatus === "ready") {
      return;
    }

    onConnectSyncStateRef.current = "idle";
    lastSectionSignatureRef.current = {};
  }, [bridgeStatus]);

  useEffect(() => {
    if (!(bridgeStatus === "ready" && instanceId)) {
      return;
    }

    if (onConnectSyncStateRef.current !== "idle") {
      return;
    }

    onConnectSyncStateRef.current = "sending";
    const currentPtt = usePushToTalkStore.getState();
    const currentPttSection = createPttSection({
      keyCode: currentPtt.code,
      platform: pttPlatform,
    });

    if (!currentPttSection) {
      recordErrorInSentry(
        new Error("Unable to resolve push-to-talk config during desktop sync"),
        {
          extra: {
            code: currentPtt.code,
            platform: pttPlatform,
          },
          tags: {
            event: "push-to-talk-sync-config-invalid",
            feature: "push-to-talk",
          },
        }
      );
    }

    if (currentPttSection) {
      logPttSelectionSync({
        bridgeMessageType: DESKTOP_SYNC_MESSAGE_TYPES.ON_CONNECT,
        keyCode: currentPtt.code,
        platform: pttPlatform,
        resolvedConfig: currentPttSection,
      });
    }

    sendSyncMessage(DESKTOP_SYNC_MESSAGE_TYPES.ON_CONNECT, {
      ...(currentPttSection ? { ptt: currentPttSection } : {}),
      presence: presenceSection,
      profile: {
        displayName: user.name,
        userId: user.id,
        ...avatarPictureUpdate,
      },
      team: teamSection,
    });
    onConnectSyncStateRef.current = "sent";
  }, [
    bridgeStatus,
    instanceId,
    avatarPictureUpdate,
    presenceSection,
    pttPlatform,
    sendSyncMessage,
    teamSection,
    user.id,
    user.name,
  ]);

  useEffect(() => {
    if (!(onConnectSyncStateRef.current === "sent" && pttSection)) {
      return;
    }

    logPttSelectionSync({
      bridgeMessageType: DESKTOP_SYNC_MESSAGE_TYPES.PTT_UPDATE,
      keyCode: code,
      platform: pttPlatform,
      resolvedConfig: pttSection,
    });

    sendSyncMessage(DESKTOP_SYNC_MESSAGE_TYPES.PTT_UPDATE, {
      ptt: pttSection,
    });
  }, [code, pttPlatform, pttSection, sendSyncMessage]);

  useEffect(() => {
    if (onConnectSyncStateRef.current !== "sent") {
      return;
    }

    sendSyncMessage(DESKTOP_SYNC_MESSAGE_TYPES.PROFILE_UPDATE, {
      profile: profileSection,
    });
  }, [profileSection, sendSyncMessage]);

  useEffect(() => {
    if (bridgeStatus === "ready") {
      return;
    }

    sidebarConnectedReportedRef.current = "idle";
  }, [bridgeStatus]);

  useEffect(() => {
    if (!(bridgeStatus === "ready" && desktopAppVersion)) {
      return;
    }

    if (sidebarConnectedReportedRef.current !== "idle") {
      return;
    }

    sidebarConnectedReportedRef.current = "sending";

    appEventCreate({
      client: "desktop",
      event: AppEventNames.SidebarConnected,
      version: desktopAppVersion,
    })
      .then(() => {
        sidebarConnectedReportedRef.current = "sent";
      })
      .catch((error) => {
        sidebarConnectedReportedRef.current = "idle";

        recordErrorInSentry(error, {
          tags: {
            event: "sidebar-connected-report-failed",
            feature: "desktop-bridge",
          },
        });
      });
  }, [bridgeStatus, desktopAppVersion]);

  return null;
};
